import {
  type BlockInviteEligibleRecipientsQuery,
  BlockInviteTargetType,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useCallback, useMemo } from 'react';

import {
  PermissionGroupInvitePickerView,
  PermissionGroupRow,
  PermissionMemberInvitePickerView,
  PermissionMemberRow,
} from '../../../modules/permissions/views/permission-invite-picker';
import * as styles from './styles.css';

export type EligibleUser =
  BlockInviteEligibleRecipientsQuery['blockInviteEligibleRecipients']['users'][number];
export type EligibleGroup =
  BlockInviteEligibleRecipientsQuery['blockInviteEligibleRecipients']['groups'][number];

export type SelectedTarget =
  | {
      type: BlockInviteTargetType.User;
      value: EligibleUser;
    }
  | {
      type: BlockInviteTargetType.Group;
      value: EligibleGroup;
    };

type SelectedUserTarget = Extract<
  SelectedTarget,
  { type: BlockInviteTargetType.User }
>;

type SelectedGroupTarget = Extract<
  SelectedTarget,
  { type: BlockInviteTargetType.Group }
>;

export const targetKey = (target: SelectedTarget) =>
  `${target.type}:${target.value.id}`;

export const userLabel = (user: EligibleUser) => user.name || user.email;

export const groupLabel = (group: EligibleGroup) => group.name;

export function splitSelectedTargetIds(targets: SelectedTarget[]) {
  return {
    userIds: targets
      .filter(target => target.type === BlockInviteTargetType.User)
      .map(target => target.value.id),
    groupIds: targets
      .filter(target => target.type === BlockInviteTargetType.Group)
      .map(target => target.value.id),
  };
}

export const BlockInviteRecipientPicker = ({
  groups,
  groupSearchText,
  isLoadingGroups,
  isLoadingUsers,
  onGroupSearchTextChange,
  onRemoveTarget,
  searchUnavailable,
  searchUnavailableMessage,
  onToggleGroup,
  onToggleUser,
  onUserSearchTextChange,
  selectedTargets,
  userSearchText,
  users,
}: {
  userSearchText: string;
  groupSearchText: string;
  onUserSearchTextChange: (value: string) => void;
  onGroupSearchTextChange: (value: string) => void;
  users: EligibleUser[];
  groups: EligibleGroup[];
  selectedTargets: SelectedTarget[];
  isLoadingUsers: boolean;
  isLoadingGroups: boolean;
  searchUnavailable: boolean;
  searchUnavailableMessage: string | null;
  onToggleUser: (user: EligibleUser) => void;
  onToggleGroup: (group: EligibleGroup) => void;
  onRemoveTarget: (target: SelectedTarget) => void;
}) => {
  const t = useI18n();
  const selectedKeys = useMemo(
    () => new Set(selectedTargets.map(targetKey)),
    [selectedTargets]
  );
  const selectedUserTargets = useMemo(
    () =>
      selectedTargets.filter(
        (target): target is SelectedUserTarget =>
          target.type === BlockInviteTargetType.User
      ),
    [selectedTargets]
  );
  const selectedGroupTargets = useMemo(
    () =>
      selectedTargets.filter(
        (target): target is SelectedGroupTarget =>
          target.type === BlockInviteTargetType.Group
      ),
    [selectedTargets]
  );
  const availableUsers = useMemo(
    () =>
      users.filter(user => {
        const key = targetKey({
          type: BlockInviteTargetType.User,
          value: user,
        });
        return !selectedKeys.has(key);
      }),
    [selectedKeys, users]
  );
  const availableGroups = useMemo(
    () =>
      groups.filter(group => {
        const key = targetKey({
          type: BlockInviteTargetType.Group,
          value: group,
        });
        return !selectedKeys.has(key);
      }),
    [groups, selectedKeys]
  );
  const handleSelectUser = useCallback(
    (user: EligibleUser) => {
      onToggleUser(user);
      onUserSearchTextChange('');
    },
    [onToggleUser, onUserSearchTextChange]
  );
  const handleSelectGroup = useCallback(
    (group: EligibleGroup) => {
      onToggleGroup(group);
      onGroupSearchTextChange('');
    },
    [onGroupSearchTextChange, onToggleGroup]
  );

  return (
    <>
      <PermissionMemberInvitePickerView
        title={t['com.streamlain.block-invite.people']()}
        placeholder={t['com.streamlain.block-invite.search-placeholder']()}
        noResultsText={t['com.streamlain.block-invite.no-results']()}
        offlineMessage={searchUnavailable ? searchUnavailableMessage : null}
        members={availableUsers}
        isSearching={isLoadingUsers}
        disabled={searchUnavailable}
        searchText={userSearchText}
        onSearchTextChange={onUserSearchTextChange}
        onSelectMember={handleSelectUser}
        roleSlot={null}
      >
        {selectedUserTargets.length > 0 ? (
          <div className={styles.selectedList}>
            {selectedUserTargets.map(target => (
              <PermissionMemberRow
                key={targetKey(target)}
                user={{
                  id: target.value.id,
                  name: userLabel(target.value),
                  email: target.value.email,
                  avatarUrl: target.value.avatarUrl,
                }}
                onRemove={() => onRemoveTarget(target)}
              />
            ))}
          </div>
        ) : null}
      </PermissionMemberInvitePickerView>

      <PermissionGroupInvitePickerView
        title={t['com.streamlain.block-invite.groups']()}
        placeholder={t['com.streamlain.block-invite.search-placeholder']()}
        noResultsText={t['com.streamlain.block-invite.no-results']()}
        offlineMessage={searchUnavailable ? searchUnavailableMessage : null}
        groups={availableGroups}
        isSearching={isLoadingGroups}
        disabled={searchUnavailable}
        searchText={groupSearchText}
        onSearchTextChange={onGroupSearchTextChange}
        onSelectGroup={handleSelectGroup}
        roleSlot={null}
      >
        {selectedGroupTargets.length > 0 ? (
          <div className={styles.selectedList}>
            {selectedGroupTargets.map(target => (
              <PermissionGroupRow
                key={targetKey(target)}
                groupName={groupLabel(target.value)}
                onRemove={() => onRemoveTarget(target)}
              />
            ))}
          </div>
        ) : null}
      </PermissionGroupInvitePickerView>
    </>
  );
};
